import {MouseEvent, ReactElement, ReactNode, useEffect, useRef, useState} from "react";
import {
    ImageItem,
    isImageItem,
    isMarginItem,
    isPageBreakItem,
    isPageTitleItem,
    isTitleItem,
    isWineItem,
    MarginItem,
    MenuFormat,
    MenuItem,
    PageBreakItem,
    PageTitleItem,
    TitleItem,
    WineItem
} from "../MenuDocumentModel";
import {TitlePreviewItem} from "./TitlePreviewItem";
import {WinePreviewItem} from "./WinePreviewItem";
import {PageBreakPreviewItem} from "./PageBreakPreviewItem";
import {PageTitlePreviewItem} from "./PageTitlePreviewItem";
import {MarginPreviewItem} from "./MarginPreviewItem";
import {ImagePreviewItem} from "./ImagePreviewItem";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuEditorActions, menuEditorSelectors} from "../editorSlice";
import {EditorItem} from "../editorUtils";
import {equals} from "ramda";
import {ReactComponent as DuplicateIcon} from "components/Icons/duplicate.svg";
import {ReactComponent as TrashIcon} from "components/Icons/trash.svg";
import {scrollIntoViewIfNeeded} from "lib/util";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {itemKeyToDndId} from "./dragndrop";
import {ReactComponent as DragHandleIcon} from "components/Icons/drag-handle.svg";
import { isLabelItem, LabelItem } from "features/label-editor/LabelDocumentModel";
import { LabelPreviewItem } from "features/label-editor/LabelPreviewItem";
import { createDocumentHandler } from "../documentHandlerUtils";

export interface EditorPreviewItemProps {
    item: EditorItem<MenuItem>
    format: MenuFormat
    degu: boolean
    isPreview?: boolean
    isSortable: boolean
}

export function EditorPreviewItem(props: EditorPreviewItemProps) {
    const item = (
        <EditableItem item={props.item}>
            <PreviewItem
                item={props.item}
                format={props.format}
                degu={props.degu}
                isPreview={props.isPreview}
            />
        </EditableItem>
    )
    if (!props.isSortable) {
        return item;
    }
    return (
        <SortableItem item={props.item}
                      format={props.format}>
            {item}
        </SortableItem>
    );
}

interface SortableItemProps {
    item: EditorItem<MenuItem>
    format: MenuFormat
    children: ReactNode
}

function SortableItem(props: SortableItemProps): ReactElement {
    const dndId = itemKeyToDndId(props.item.itemKey);
    const sortable = useSortable({
        id: dndId,
        transition: null
    });
    const style = {
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0 : 1
    }

    return (
        <div ref={sortable.setNodeRef}
             className="relative"
             style={style}>
            {props.children}
            <button className={`absolute inset-y-0 flex items-center justify-center touch-none
                                ${props.format === 'a4-half' ? ' -left-4 ' : ' ' }
                                ${props.format === 'a5' ? '-left-7' : ''}
                                ${props.format === 'a4' ? '-left-10' : ''}`}
                    {...sortable.listeners}
                    {...sortable.attributes}>
                <DragHandleIcon className={`text-gray-1
                                            ${props.format === 'a4' ? 'w-6 h-6 ' : ''}
                                            ${props.format === 'a4-half' ? 'w-4 h-4 ' : ''}
                                            ${props.format === 'a5' ? 'w-4 h-4 ' : ''}`}/>
            </button>
        </div>
    );
}

interface EditableItemProps {
    item: EditorItem<MenuItem>
    children: ReactNode
}

function EditableItem(props: EditableItemProps): ReactElement {
    const dispatch = useAppDispatch();
    const document = useAppSelector(menuEditorSelectors.document);

    const ref = useRef<HTMLDivElement>(null);
    const setFocus = () => dispatch(menuEditorActions.setFocusedItem(props.item.itemKey));
    const focusedItem = useAppSelector(menuEditorSelectors.focusedItem);
    const isFocused = equals(focusedItem?.itemKey, props.item.itemKey);
    const [height, setHeight] = useState<number>(ref.current?.offsetHeight || 0);
    const [editButtonsDisplayMode, setEditButtonsDisplayMode] = useState<"col" | "row">("col");

    useEffect(() => {
        setHeight(ref.current?.offsetHeight || 0);
    }, [props.item]);

    useEffect(() => {
        setEditButtonsDisplayMode(height > 70 ? "col" : "row");
    }, [height]);

    // scroll into view if focused and not in view
    useEffect(() => {
        if (isFocused && ref.current != null) {
            scrollIntoViewIfNeeded(ref.current);
        }
    }, [isFocused]);
    
    if (!document) return <></>;

    const duplicate = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        return dispatch(menuEditorActions.addItems({
            items: [props.item.menuItem],
            after: props.item.itemKey
        }));
    };

    const remove = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        dispatch(menuEditorActions.removeItem(props.item.itemKey));
    }

    const docHandler = createDocumentHandler(document);
    const itemStyles = docHandler.getEditableItemPropsAndStyling(props.item, isFocused);
    
    return (
        <div className={`relative ${itemStyles.className}`}
            style={itemStyles.style}
             onClick={e => {
                 e.stopPropagation();
                 setFocus();
             }}
             ref={ref}>
            {props.children}

            {/* duplicate / trash icons */}
            {itemStyles.hasControls&&<div className={`absolute -inset-y-2 -right-2 rounded-r-xl bg-warm-gray text-secondary
                             flex ${editButtonsDisplayMode === 'col' ? 'flex-col justify-evenly px-3' : 'gap-3 px-3'}
                             ${isFocused ? '' : 'hidden'}`}>
                <button onClick={duplicate}>
                    <DuplicateIcon className="h-5"/>
                </button>
                <button onClick={remove}>
                    <TrashIcon className="h-5"/>
                </button>
            </div>}
        </div>
    );
}

interface PreviewItemProps {
    item: EditorItem<MenuItem>
    format: MenuFormat
    degu?: boolean
    isPreview?: boolean
}

export function PreviewItem(props: PreviewItemProps): ReactElement | null {
    if (isPageTitleItem(props.item.menuItem)) {
        return (
            <PageTitlePreviewItem
                format={props.format}
                title={props.item as EditorItem<PageTitleItem>}
            />
        );
    }
    if (isTitleItem(props.item.menuItem)) {
        return (
            <TitlePreviewItem
                format={props.format}
                degu={props.degu || false}
                title={props.item as EditorItem<TitleItem>}
            />
        );
    }
    if (isMarginItem(props.item.menuItem)) {
        return (
            <MarginPreviewItem
                showBorders={true}
                format={props.format}
                margin={props.item as EditorItem<MarginItem>}
            />
        );
    }
    if (isPageBreakItem(props.item.menuItem)) {
        return (
            <PageBreakPreviewItem
                format={props.format}
                pageBreak={props.item as EditorItem<PageBreakItem>}
            />
        );
    }
    if (isImageItem(props.item.menuItem)) {
        return (
            <ImagePreviewItem
                format={props.format}
                image={props.item as EditorItem<ImageItem>}
            />
        );
    }
    if (isWineItem(props.item.menuItem)) {
        return (
            <WinePreviewItem
                format={props.format}
                degu={props.degu || false}
                wineItem={props.item as EditorItem<WineItem>}
                isPreview={props.isPreview}
            />
        );
    }
    if (isLabelItem(props.item.menuItem)) {
        return (
            <LabelPreviewItem 
                labelItem={props.item as EditorItem<LabelItem>}
                isPreview={props.isPreview}
            />
        )
    }

    return null;
}
